import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Room Type master (Masters → Room Types). Typed split out of legacy
 * `tbl_MiscellaneousMaster` where `FieldFilter = 'RoomType'`. Common codes:
 * STD (Standard), DLX (Deluxe), STE (Suite), VIL (Villa), POOL (Pool view),
 * SEA (Sea view). Linked from `itinerary_components` for HOTEL components.
 */

export const RoomTypeListItemSchema = z.object({
  id: UuidSchema,
  legacyLookupId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type RoomTypeListItem = z.infer<typeof RoomTypeListItemSchema>;

export const RoomTypeListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type RoomTypeListQuery = z.infer<typeof RoomTypeListQuerySchema>;

export const RoomTypeListResponseSchema = paginatedResponseSchema(RoomTypeListItemSchema);
export type RoomTypeListResponse = z.infer<typeof RoomTypeListResponseSchema>;

export const RoomTypeDetailSchema = RoomTypeListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type RoomTypeDetail = z.infer<typeof RoomTypeDetailSchema>;

const RoomTypeCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyLookupId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateRoomTypeRequestSchema = RoomTypeCoreFieldsSchema;
export type CreateRoomTypeRequest = z.infer<typeof CreateRoomTypeRequestSchema>;

export const UpdateRoomTypeRequestSchema = RoomTypeCoreFieldsSchema.partial();
export type UpdateRoomTypeRequest = z.infer<typeof UpdateRoomTypeRequestSchema>;
